/**
 * Pagina de Revisao de Documentos (Admin)
 * Route: /dashboard/documentos
 */

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Button, Badge, useToast,
  DataTable,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  type DataTableColumn, type BadgeVariant,
} from "@payflow/ui";

const API_BASE = (import.meta as any).env?.VITE_API_URL || "http://localhost:8787";
const TOKEN_KEY = "payflow_token";

// ============ Types ============

type DocumentStatus = "pendente" | "aprovado" | "reprovado" | "reenviar";

interface DocumentItem {
  id: string;
  leadId: string;
  clienteId: string | null;
  campo: string;
  tipo: string;
  storageKey: string;
  fileUrl?: string;
  status: DocumentStatus;
  motivoReprovacao: string | null;
  versao: number;
  createdAt: string;
  updatedAt: string;
  leadNome: string | null;
  leadCpf: string | null;
  clienteNome: string | null;
}

type TabFilter = "todos" | "pendente" | "aprovado" | "reprovado";

const STATUS_BADGE_VARIANT: Record<DocumentStatus, BadgeVariant> = {
  pendente: "warning",
  aprovado: "success",
  reprovado: "error",
  reenviar: "info",
};

// ============ API ============

async function apiFetch(path: string, options?: RequestInit) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error?.message || "Erro na requisicao");
  return data;
}

// ============ Component ============

export function DocumentsReviewPage() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const getStatusLabel = (status: DocumentStatus): string => {
    const map: Record<DocumentStatus, string> = {
      pendente: t("documents_review.status_pending"),
      aprovado: t("documents_review.status_approved"),
      reprovado: t("documents_review.status_rejected"),
      reenviar: t("documents_review.status_resubmit"),
    };
    return map[status];
  };
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabFilter>("pendente");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Selection for bulk approve
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Preview modal
  const [previewDoc, setPreviewDoc] = useState<DocumentItem | null>(null);

  // Reject modal
  const [rejectDoc, setRejectDoc] = useState<DocumentItem | null>(null);
  const [rejectMotivo, setRejectMotivo] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = activeTab !== "todos" ? `&status=${activeTab}` : "";
      const data = await apiFetch(`/v1/admin/documents?page=${page}&limit=${pageSize}${statusParam}`);
      setDocuments(data.data || []);
      setTotal(data.meta?.total || 0);
    } catch {
      toast({ variant: "error", title: t("documents_review.load_error") });
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, pageSize, toast, t]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);
  useEffect(() => { setPage(1); setSelectedIds(new Set()); }, [activeTab]);

  // Actions
  const handleApprove = async (id: string) => {
    try {
      await apiFetch(`/v1/admin/documents/${id}/approve`, { method: "PUT" });
      toast({ variant: "success", title: t("documents_review.approved") });
      loadDocuments();
    } catch {
      toast({ variant: "error", title: t("documents_review.approve_error") });
    }
  };

  const handleReject = async () => {
    if (!rejectDoc || !rejectMotivo.trim()) return;
    setRejecting(true);
    try {
      await apiFetch(`/v1/admin/documents/${rejectDoc.id}/reject`, {
        method: "PUT",
        body: JSON.stringify({ motivo: rejectMotivo }),
      });
      toast({ variant: "success", title: t("documents_review.rejected") });
      setRejectDoc(null);
      setRejectMotivo("");
      loadDocuments();
    } catch {
      toast({ variant: "error", title: t("documents_review.reject_error") });
    } finally {
      setRejecting(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    try {
      await apiFetch("/v1/admin/documents/bulk-approve", {
        method: "POST",
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      toast({ variant: "success", title: t("documents_review.bulk_approved", { count: selectedIds.size }) });
      setSelectedIds(new Set());
      loadDocuments();
    } catch {
      toast({ variant: "error", title: t("documents_review.bulk_error") });
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pendingDocs = documents.filter((d) => d.status === "pendente");
    if (selectedIds.size === pendingDocs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingDocs.map((d) => d.id)));
    }
  };

  const getFileUrl = (doc: DocumentItem) => {
    return doc.fileUrl || "";
  };

  const isImage = (tipo: string) => tipo === "image";

  const tabs: { id: TabFilter; label: string }[] = [
    { id: "pendente", label: t("documents_review.tab_pending") },
    { id: "todos", label: t("documents_review.tab_all") },
    { id: "aprovado", label: t("documents_review.tab_approved") },
    { id: "reprovado", label: t("documents_review.tab_rejected") },
  ];

  const columns: DataTableColumn<DocumentItem>[] = [
    {
      key: "cliente",
      header: "Cliente / Lead",
      render: (doc) => (
        <div>
          <p className="font-medium text-foreground">
            {doc.clienteNome || doc.leadNome || "Desconhecido"}
          </p>
          <p className="text-xs text-muted-foreground">
            {doc.leadCpf
              ? doc.leadCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
              : "-"}
          </p>
        </div>
      ),
    },
    {
      key: "campo",
      header: "Documento",
      render: (doc) => (
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" size="md">{doc.campo}</Badge>
          {doc.versao > 1 && (
            <span className="text-xs text-muted-foreground">v{doc.versao}</span>
          )}
        </div>
      ),
    },
    {
      key: "preview",
      header: "Preview",
      className: "w-16",
      render: (doc) => (
        <button
          onClick={(e) => { e.stopPropagation(); setPreviewDoc(doc); }}
          className="h-10 w-10 overflow-hidden rounded border border-border hover:opacity-80 transition-opacity"
        >
          {isImage(doc.tipo) ? (
            <img src={getFileUrl(doc)} alt={doc.campo} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted text-xs text-muted-foreground">PDF</div>
          )}
        </button>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (doc) => {
        const variant = STATUS_BADGE_VARIANT[doc.status];
        return (
          <div>
            <Badge variant={variant} dot size="md">{getStatusLabel(doc.status)}</Badge>
            {doc.motivoReprovacao && (
              <p className="mt-1 max-w-[200px] truncate text-xs text-red-600">{doc.motivoReprovacao}</p>
            )}
          </div>
        );
      },
    },
    {
      key: "data",
      header: "Data",
      render: (doc) => (
        <span className="text-sm text-muted-foreground">
          {new Date(doc.createdAt).toLocaleDateString("pt-BR")}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      className: "w-32",
      render: (doc) => doc.status === "pendente" ? (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="outline" onClick={() => handleApprove(doc.id)} className="h-7 text-xs text-green-600 hover:bg-green-50">
            {t("documents_review.approve")}
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setRejectDoc(doc); setRejectMotivo(""); }} className="h-7 text-xs text-red-600 hover:bg-red-50">
            {t("documents_review.reject")}
          </Button>
        </div>
      ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("documents_review.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("documents_review.description")}
          </p>
        </div>
        {selectedIds.size > 0 && (
          <Button onClick={handleBulkApprove}>
            {t("documents_review.approve_selected")} ({selectedIds.size})
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg border border-border bg-muted p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.label}
            {tab.id === "pendente" && total > 0 && activeTab === "pendente" && (
              <span className="ml-1.5 rounded-full bg-yellow-500 px-1.5 py-0.5 text-xs text-white">
                {total}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={documents}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        loading={loading}
        emptyMessage={t("documents_review.empty")}
        selectedRows={activeTab === "pendente" ? selectedIds : undefined}
        onSelectRow={activeTab === "pendente" ? toggleSelect : undefined}
        onSelectAll={activeTab === "pendente" ? toggleSelectAll : undefined}
        rowKey={(row) => row.id}
        onRowClick={(doc) => setPreviewDoc(doc)}
      />

      {/* Preview Modal */}
      {previewDoc && (
        <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                {previewDoc.campo} - {previewDoc.clienteNome || previewDoc.leadNome}
              </DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center p-4">
              {isImage(previewDoc.tipo) ? (
                <img src={getFileUrl(previewDoc)} alt={previewDoc.campo} className="max-h-[70vh] rounded-lg object-contain" />
              ) : (
                <iframe src={getFileUrl(previewDoc)} className="h-[70vh] w-full rounded-lg border" title={previewDoc.campo} />
              )}
            </div>
            {previewDoc.status === "pendente" && (
              <DialogFooter>
                <Button variant="outline" onClick={() => { setRejectDoc(previewDoc); setRejectMotivo(""); setPreviewDoc(null); }} className="text-red-600">
                  {t("documents_review.reject")}
                </Button>
                <Button onClick={() => { handleApprove(previewDoc.id); setPreviewDoc(null); }}>
                  {t("documents_review.approve")}
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Reject Modal */}
      {rejectDoc && (
        <Dialog
          open={!!rejectDoc}
          onOpenChange={() => setRejectDoc(null)}
          dirty={rejectMotivo.trim() !== ""}
          onSave={async () => { await handleReject(); }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("documents_review.reject_dialog_title")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Documento: <strong>{rejectDoc.campo}</strong> de{" "}
                <strong>{rejectDoc.clienteNome || rejectDoc.leadNome}</strong>
              </p>
              <div>
                <label className="mb-1 block text-sm font-medium">{t("documents_review.reject_reason_label")}</label>
                <textarea
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={3}
                  value={rejectMotivo}
                  onChange={(e) => setRejectMotivo(e.target.value)}
                  placeholder={t("documents_review.reject_reason_placeholder")}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDoc(null)}>{t("common.cancel")}</Button>
              <Button variant="destructive" onClick={handleReject} disabled={rejecting || !rejectMotivo.trim()}>
                {rejecting ? t("documents_review.rejecting") : t("documents_review.reject_confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
